import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/classnames';
import { Spinner } from '@/components/spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-[#2563EB] text-action-primary hover:bg-[#6D96F2] active:hover:bg-[#1A46A7] disabled:bg-[#BBCFF9] disabled:dark:bg-[#9BB7F6] disabled:text-[#FFFFFF]',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        button: 'bg-[#57E8BA] rounded-full text-[#000000] text-[1.2rem] font-regular',
        logout: 'flex items-center gap-2 text-[red] hover:text-[red]',
        manageRole:
          'bg-[#57E8BA] px-[1.5rem] py-[0.75rem] text-[#000000] rounded-[1.4rem] text-[1.2rem]',
      },
      size: {
        default: 'py-[0.6rem] px-[1.4rem]',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        size40: 'h-[4rem] rounded-[0.8rem] px-[1.4rem]',
        size48: 'h-[4.8rem] rounded-[1rem] px-[1.6rem]',
        button: 'py-[0.8rem] px-[0.22rem] w-[8.3rem] h-[3.3rem]',
        dropdown: 'h-[3.3rem] px-[1.4rem] py-[0.8rem] min-w-[10.5rem] rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Spinner width={16} height={16} className="mr-2" />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
